import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swords } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import { useMeQuery } from '@/services/qbotApi';

/**
 * Clash Royale entry pill for the Hub. Mirrors RiotHubTile shape so the
 * Hub's pills row stays visually consistent.
 *
 * Conditional rendering: we render the tile only when the user is
 * authenticated against the qbot-api (`/me` succeeds — i.e. a fresh
 * Logto JWT was minted for the QBot audience). If the tier is `free`
 * with no `linkedAccounts`, the tile copy switches to "Link Clash Royale"
 * and the link routes to the picker; once linked, copy becomes "Open"
 * and routes to the CR home.
 *
 * To keep the Hub paint-fast, the underlying query lives in RTK Query
 * cache (`keepUnusedDataFor: 300`), so subsequent renders are instant.
 */
export function CRHubTile() {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();

  // The /me probe is cheap (no upstream call), and returns 404 only if
  // Logto/QBot are unreachable. `error` of any kind → hide the tile to
  // avoid a "broken" CTA on first render.
  const { data, error, isLoading } = useMeQuery();

  if (isLoading) return null;
  if (error) return null;
  if (!data) return null;

  const linked = data.linkedAccounts.length > 0;

  return (
    <Link
      to={linked ? '/cr' : '/cr/link'}
      onClick={() => haptics.impact('light')}
      className={cn(
        'flex h-11 items-center justify-center gap-2 rounded-lg border-2 border-brand px-6',
        'bg-bg-secondary text-xs font-bold uppercase tracking-wide text-brand',
        'transition-opacity active:opacity-80',
      )}
    >
      <Swords aria-hidden className="h-5 w-5" strokeWidth={2.5} />
      <span>{linked ? t('hubTile.open') : t('hubTile.link')}</span>
    </Link>
  );
}
